/**
 * Wrap ™ and ® so they can be sized and aligned.
 *
 * CSS cannot select a character, and the symbols live inside plain-text fields
 * (product titles, Product Specs, the tagline metafield) where markup is not an
 * option — so a title can never carry its own <sup>. Wrapping at runtime is the
 * only way to reach all of them from one place.
 *
 * Styling is in apps.css (.tm-sym). This file only adds the hooks.
 *
 * Deliberately conservative:
 *   - text nodes only, so attributes and markup are untouched
 *   - skips script/style/input/textarea/code/pre, existing <sup>, and anything
 *     already wrapped, so re-running is safe
 *   - re-runs on Shopify section renders and on DOM additions (cart drawer,
 *     quick view, predictive search), because those arrive after load
 *   - if it fails or JS is off, the symbols simply stay their natural size
 */
(function () {
  'use strict';

  var SYMBOLS = /[™®]/; // ™ ®
  var SPLIT = /([™®])/;

  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1,
    OPTION: 1, CODE: 1, PRE: 1, SUP: 1, SUB: 1, SVG: 1, TITLE: 1
  };

  /**
   * Every ancestor is checked, up to the document — deliberately NOT stopping at
   * the root passed to wrap(). The observer hands us freshly created .tm-sym
   * spans as roots, and a guard that stopped at the root would never see the
   * root's own class: it would re-wrap its own output, nest a span per pass and
   * grow without limit.
   */
  function skippable(node) {
    for (var el = node.parentNode; el && el.nodeType === 1; el = el.parentNode) {
      if (SKIP_TAGS[el.nodeName]) return true;
      if (el.classList && el.classList.contains('tm-sym')) return true;
      if (el.isContentEditable) return true;
    }
    return false;
  }

  function wrap(root) {
    if (!root || root.nodeType !== 1) return;

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var targets = [];
    var node;
    while ((node = walker.nextNode())) {
      if (!SYMBOLS.test(node.nodeValue)) continue;
      if (skippable(node)) continue;
      targets.push(node);
    }

    for (var i = 0; i < targets.length; i++) {
      var text = targets[i];
      var parts = text.nodeValue.split(SPLIT);
      if (parts.length < 2) continue;

      var frag = document.createDocumentFragment();
      for (var j = 0; j < parts.length; j++) {
        var part = parts[j];
        if (!part) continue;
        if (part === '™' || part === '®') {
          var span = document.createElement('span');
          span.className = 'tm-sym';
          span.textContent = part;
          frag.appendChild(span);
        } else {
          frag.appendChild(document.createTextNode(part));
        }
      }
      if (!text.parentNode) continue;

      /**
       * In a flex or grid container, a bare text node is an anonymous item.
       * Replacing it with "text + span + text" makes THREE items, so the
       * container's gap opens up either side of the symbol — the product
       * breadcrumb renders as "TurboFry ® Touch 4-Quart" with 12-18px holes.
       * Keeping the fragment inside one neutral span keeps it one item.
       */
      var display = '';
      try {
        display = window.getComputedStyle(text.parentNode).display;
      } catch (e) { /* detached node: fall through to the plain path */ }

      if (display.indexOf('flex') !== -1 || display.indexOf('grid') !== -1) {
        var holder = document.createElement('span');
        holder.appendChild(frag);
        text.parentNode.replaceChild(holder, text);
      } else {
        text.parentNode.replaceChild(frag, text);
      }
    }
  }

  function run() {
    wrap(document.body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Sections re-render in the theme editor and via the Section Rendering API.
  document.addEventListener('shopify:section:load', function (e) {
    wrap(e.target);
  });

  // Drawers, quick view and predictive search inject markup after load. Batched
  // on a frame so a burst of mutations costs one pass, not one pass each.
  if (window.MutationObserver) {
    var queued = [];
    var scheduled = false;

    var flush = function () {
      scheduled = false;
      var batch = queued;
      queued = [];
      for (var i = 0; i < batch.length; i++) wrap(batch[i]);
    };

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var el = added[j];
          if (el.nodeType !== 1) continue;
          // our own spans: ignore, or every wrap schedules another pass
          if (el.classList && el.classList.contains('tm-sym')) continue;
          queued.push(el);
        }
      }
      if (queued.length && !scheduled) {
        scheduled = true;
        requestAnimationFrame(flush);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
